<template>
  <div class="pupils">
    <innertopbar @close="closeWindow()" class="mb-10">
      <template v-slot:breadcrumb>
        <router-link to="/health/taskgroups"> Task Groups </router-link>
        <i class="carret fas fa-caret-right"></i> {{ title }}
      </template>

      <template v-slot:listitems>
        <li  @click="openInfo()" v-if="groupHasFile" 
        class="action-list-item inline-block hover:opacity-100"
>
          <i class="pt-3 text-xl icon fas fa-file-pdf cursor-pointer"></i>
        </li>
      </template>
    </innertopbar>

    <FilterBar @searchChanged="setSearchValue">
      <template v-slot:filter>
        <div>
          <ClassDropdown
            :classSelected="filterValue.group"
            @classValueChanged="updateFilteredClass"
          ></ClassDropdown>
        </div>
        <div>
          <button
            type="button"
            class="filter-bar-button focus:outline-none hover:opacity-75"
            v-bind:class="
              filterHasValues ? 'filter-item-button-on' : 'bg-white'
            "
            @click="showFilter()"
          >
            <i class="fas fa-filter"></i> Filters
          </button>
        </div>
      </template>

      <template v-slot:actions> </template>
    </FilterBar>

    <PupilFilterModal
      class=""
      v-if="filterIsVisible"
      :values="filterValue"
      @apply-filter="applyFilter"
      @close="showFilter"
    >
    </PupilFilterModal>

    <p v-if="showMessage" class="save-notice ml-6 mb-6 font-bold">
      {{ msg }} <i class="far fa-check-circle"></i>
    </p>

    <table
      class="w-95p mx-auto mb-2 h-14 pt-2 align-text-bottom bg-white rounded text-left text-gray-400"
    >
      <thead class="bg-gray-100 w-full">
        <tr>
          <th class="pl-2  w-3/12">
            Pupil Name
            <i
              class="fas fa-sort tbh-sort"
              v-bind:class="
                'name' === sortBy ? 'text-blue-500' : 'text-gray-400'
              "
            ></i>
          </th>
          <th
            v-for="(col, index) in tasks"
            :key="index"
            class="text-xs text-center w-2 py-2 "
            style="min-width:2.5;max-width:2.5em;"
          >
           <div class="font-bold">{{ col.title }}</div> 
          <div class="grid grid-cols-2 space-x-1 w-2/3 mx-auto">
            <div  @click="openTaskInfo(col)" v-if="taskHasFile(col)">  
              <i class="pt-3 text-xl icon fas fa-file-pdf cursor-pointer hover:opacity-75"></i></div>
            <div  @click="openTaskVideo(col)" v-if="taskHasVideo(col)"> 
             
              <i class="pt-3 text-xl icon fas fa-file-video cursor-pointer hover:opacity-75"></i></div> 
          </div>
          </th>
        </tr>
      </thead>
    </table>

    <div class="table-wrapper">
      <div v-if="errored" class="empty ml-10 mt-4">
        <p>
          We are not able to retrieve this information at the moment, please try
          back later
        </p>
      </div>

      <div id="content-area" v-else>
        <div
          style="width: 100%; text-align: center"
          class="spin-holder mt-4"
          v-if="loading"
        >
          <i class="spinner fa fa-spinner fa-spin"></i>
          <p>Loading Pupils</p>
        </div>

        <div v-else>
          <table
            v-if="componentLoaded"
            class="table-wrapper mx-auto bg-white w-95p rounded shadow-sm"
          >
            <tbody>
              <tr v-for="pupil in filteredList" :key="pupil.identifier">
                <td class="w-3/12 pl-2">{{ pupil.name }}</td>
                <td
                  class="text-xs text-center  w-2"
                  style="max-width:2.5em;"
                  v-for="task in tasks"
                  :key="task.id"
                >
                <template v-if="group.layout === 'check'">
                  <div class="" @click="setTask(pupil, task, 'true')">
                    <i
                    class="icon list"
                    v-bind:class="hasOutcome(pupil, task)"

                  />
                  </div>
                 
                </template>
                <template v-else>
                  <button
              v-for="item in maturities"
              :key="item.letter"
              type="button"
              class="
              cursor-pointer hover:opacity-50
                border-none
                px-2
                w-3
                p-1
                mt-1
                mr-1
                rounded
                text-xs
                font-medium
                hover:text-white
              "
              style="min-width:2em;"
              v-bind:class="setMaturityClass(pupil, task, item)"
              @click="setTask(pupil, task, item.value)"
            >
              {{ item.letter }}
            </button>
                </template>
                  
                </td>
              </tr>
            </tbody>
          </table>

          <p class="m-5 mx-12" v-else>
            We could not find any data for your given selection.
          </p>
        </div>

        <div class="w-95p mx-auto">
          <Pagination
            v-if="componentLoaded"
            :total-pages="totalPages"
            :total="totalResultsCount"
            :current-page="currentPage"
            :has-more-pages="hasMorePages"
            :pageSize="pageSize"
            @pagechanged="showMore"
            class="mt-4 mb-12"
          >
          </Pagination>
        </div>
      </div>
    </div>

    <ModalFile
      :url="selectedDocument.file_url"
      :title="selectedDocument.title"
      :format="'taskgroup'"
      @close="showModalFile = false"
      v-if="selectedDocument != undefined && showModalFile"
      v-show="showModalFile"
    ></ModalFile>

    <ModalVideo
      :url="selectedVideo.video_url_b"
      @close="showModalVideo = false"
      v-if="selectedVideo != undefined && showModalVideo"
      v-show="showModalVideo"
    ></ModalVideo>

  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { PupilService } from "@/services/pupil.service";
import { TaskService } from "@/services/task.service";

const innertopbar = () => import("@/components/InnerTopBar.vue");

const ClassDropdown = () => import("@/components/pupil/ClassDropdown.vue");

const Pagination = () => import("@/components/helpers/Pagination.vue");
const PupilFilterModal = () => import("@/components/helpers/PupilFilter.vue");
const FilterBar = () => import("@/components/FilterBarNavigation.vue");
const ModalFile = () => import("@/components/ModalFile.vue");
const ModalVideo = () => import("@/components/ModalVideo.vue");

export default {
  name: "pupils",
  mixins: [],
  props: {
    group: { type: Object, required: true },
  },
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
    }),
    title() {
      return this.group != undefined ? this.group.title : ""
    },
    hasMorePages() {
      return this.pagination.has_more === true;
    },
    totalResultsCount() {
      return this.pagination.total_count;
    },
    totalPages() {
      return this.pagination.total_pages;
    },
    componentLoaded() {
      return this.filteredList.length > 0;
    },
    showMessage() {
      return this.msg != undefined && this.msg.length > 0;
    },
    searchValue() {
      return this.search;
    },
    filteredList() {
      let vm = this;
      let count = this.results.length
      return _.orderBy(vm.pupils, [vm.sortBy, "name"], [vm.sortDirection]);
    },
    filterHasValues() {
      var value = this.filterValue;
      if (
        value.group != undefined ||
        value.years != undefined ||
        value.types != undefined ||
        (value.tags != undefined && value.length > 2)
      ) {
        return true;
      }
    },
    classSelectedValue() {
      return this.filterValue.group;
    },
    groupHasFile() {
      if (this.group != undefined) {
        return this.group.file_url != undefined || this.group.file_url != null
      }
    },
  },
  data() {
    return {
      filterIsVisible: false,
      pupils: [],
      tasks: [],
      results: [],

      loading: false,
      errored: false,

      page: 1,
      pageSize: 48,
      currentPage: 1,
      pagination: {},

      search: "",
      filterValue: {},
      maturities: [],

      sortBy: "yeargroup",
      sortDirection: "asc",

      showModalFile: false,
      selectedDocument: undefined,
      showModalVideo: false,
      selectedVideo: undefined,
    };
  },
  created() {
    if (this.group == undefined) {
      this.closeWindow();
      return;
    } 

    this.maturities = [
    { value: "imature", name: "Imature", letter: "I" },
    { value: "developing", name: "Developing", letter: "D" },
    { value: "mature", name: "Mature", letter: "M" }
    ]

    TaskService.getTasksByGroup(this.group.id)
      .then((response) => (this.tasks = response.data.data))
      .catch((err) => {
        this.errored = true;
      })
      .finally(() => {
        this.fetchResults(this.group.id);
      });
  },
  mounted() {
    this.searchPupils();
  },
  methods: {
    fetchResults() {
      TaskService.getResultsByGroup(this.group.id)
        .then((response) => (this.results = response.data.data))
        .catch((err) => {
          this.errored = true;
        });
    },
    searchPupils() {
      this.loading = true;

      PupilService.getWithFilter(this.filterValue, this.page, this.pageSize)
        .then((response) => this.buildPupils(response.data))
        .catch((err) => {
          console.log("pupils " + err);
          this.errored = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildPupils(data) {
      this.pupils = data.data;
      this.pagination = data.meta.pagination;
    },
    checkPupilOutcome(pupil, task) {
      let finds = this.results.filter((grade) => {
        return (
          grade.register_identifier === pupil.identifier &&
          grade.task_identifier === task.identifier
        );
      });
      return finds;
    },
    hasOutcome(pupil, task) {
      if (this.checkPupilOutcome(pupil, task).length === 0) {
        return "fas fa-times-circle text-gray-300";
      } else {
        return "fas fa-check-circle text-green-400";
      }
    },
    setMaturityClass(pupil, task, mature) {
      const list = this.checkPupilOutcome(pupil, task)
      const last2 = list.slice(-2)
      const found = last2.pop()
      if ( found && found.result === mature.value) {
          return "bg-green-300 text-white "
      }

      if (last2.length >= 1 && last2[0].result == mature.value) {
        return "bg-orange-200 text-gray-500 "
      }

      return "bg-gray-300 text-gray-500 "
    },
    setTask(pupil, task, value) {
      let vm = this;

      let list = this.checkPupilOutcome(pupil, task);
      const found = list.pop()
      if (found === undefined) {
        this.addResult(pupil, task, value)
      } else if (this.group.layout != 'check' && found.result != value) {
        this.addResult(pupil, task, value)
      } else if (this.group.layout === 'check') {
        TaskService.deleteResult(found)
          .then((response) => {
            vm.results = vm.results.filter(
              (r) => r.identifier !== found.identifier
            );
          })
          .catch((err) => {
            console.log("GRADE DELETE error " + err);
          });
      }
    },
    addResult(pupil, task, value) {
      let vm = this;
      let payload = {
          register_identifier: pupil.identifier,
          task_identifier: task.identifier,
          result: value
        };

        TaskService.addResult(payload)
          .then((response) => {
            payload.identifier = response.data.identifier;

            vm.results.push(payload);
          })
          .catch((err) => {
            console.log(err);
          });
    },
    showFilter() {
      this.filterIsVisible = !this.filterIsVisible;
    },
    updateFilteredClass(value) {
      if (value != undefined) {
        this.filterValue.group = value;
      } else {
        this.filterValue.group = {};
      }

      this.showMore({ page: 1, size: this.pageSize });
    },
    applyFilter(filters) {
      this.filterValue = filters;
      this.updateFilteredClass(filters.group);

      this.showFilter();
      this.showMore({ page: 1, size: this.pageSize });
    },
    setSearchValue(value, time = 300) {
      this.search = value;
      this.filterValue.title = this.search;

      let vm = this;
      if (vm.search.length === 0 || vm.search.length >= 1) {
        clearTimeout(vm.timeout);
        vm.timeout = setTimeout(() => {
          vm.showMore({ page: 1, size: this.pageSize });
        }, time);
      }
    },
    showMore({ page, size }) {
      this.page = page;
      this.currentPage = page;
      this.pageSize = size;
      this.searchPupils();
      window.scrollTo(0, 0);
    },
    sort(s) {
      if (s === this.sortBy) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      }
      this.sortBy = s;
    },
    closeWindow() {
      this.$router.push({ path: "/health/taskgroups" });
      window.scrollTo(0, 0);
    },
    openInfo() {
      this.showModalFile = true
      this.selectedDocument = group
    },
    openTaskInfo(task) {
      this.showModalFile = true
      this.selectedDocument = task
    },
    taskHasFile(task) {
      return task.file_url != undefined || task.file_url != null
    },

    openTaskVideo(task) {
      this.showModalVideo = true
      this.selectedVideo = task
    },
    taskHasVideo(task) {
      return task.video_url_b != undefined || task.video_url_b != null
    }
  },
  components: {
    PupilFilterModal,
    FilterBar,
    Pagination,
    ClassDropdown,
    innertopbar,
    ModalFile,
    ModalVideo
  },
};
</script>

<style scoped>

.table-wrapper td {
  padding-left: 0.0em !important;
  padding-right: 0.0em !important;
}
</style>
